import api from '@/services/api'

export type CheckoutFulfillmentMethod = 'pickup' | 'delivery' | 'shipping'

export type PaymentMethodType = 'pago_movil' | 'transferencia' | 'zelle' | 'efectivo' | 'tarjeta' | 'binance' | 'otro'

export interface PaymentAccountConfig {
  bankName?: string
  accountType?: string
  accountNumber?: string
  holderName?: string
  idNumber?: string
  phone?: string
  email?: string
  notes?: string
}

export interface PaymentMethodConfig {
  type: PaymentMethodType
  label: string
  currency?: string | null
  instructions?: string | null
  accounts?: PaymentAccountConfig[]
}

export interface CheckoutFulfillmentConfig {
  enabled: boolean
  askMethod: boolean
  askAddress: boolean
  methodPrompt: string
  addressPrompt: string
  pickupInstructions: string
  deliveryInstructions: string
  shippingInfo: string | null
  shippingZones: string[]
  suggestAddons?: boolean
  localDeliveryTime?: string | null
  deliveryTime?: string | null
  slaValidationTime?: string | null
  /** Tarifas y umbrales de envío gratis en USD; `null` = sin configurar. */
  localShippingCost?: number | null
  nationalShippingCost?: number | null
  freeShippingThreshold?: number | null
  nationalFreeThreshold?: number | null
  /** Zonas con costo de delivery propio (USD). */
  deliveryZones?: Array<{ name: string; fee: number }>
  /** Minutos de espera para responder una duda del asistente; `null` = plazo por defecto. */
  staffConsultTimeoutMinutes?: number | null
  /** Minutos de espera de una comanda antes del aviso ámbar en el tablero. */
  orderWaitWarnMinutes?: number | null
  /** Minutos de espera de una comanda antes de la alerta roja en el tablero. */
  orderWaitCriticalMinutes?: number | null
  /** Métodos de pago del negocio con sus datos de cuenta. */
  paymentMethods?: PaymentMethodConfig[]
  /** Regla: el asistente puede enviar los datos bancarios por el chat. */
  sharePaymentDetails?: boolean
}

export interface FaqBridgeConfig {
  productBridgeText: string
  multipleProductsBridgeText: string
  genericBridgeText: string
  productStatementText: string
  multipleProductsStatementText: string
  genericStatementText: string
}

export interface FaqBridgePreset {
  id: string
  label: string
  description: string
  config: FaqBridgeConfig
}

export interface EscalationConfig {
  handoffRecipientLabel: string
  escalationMessage: string
  maxResponsesMessage: string
}

export interface EscalationPreset {
  id: string
  label: string
  description: string
  config: EscalationConfig
}

export const DEFAULT_FAQ_BRIDGE_CONFIG: FaqBridgeConfig = {
  productBridgeText:
    '¡Excelente elección con la *{{product}}*! Si te parece, tomamos tus datos y coordinamos la entrega con nuestro asesor. ¿Avanzamos?',
  multipleProductsBridgeText:
    'Con gusto. ¿Te gustaría que comparemos *{{productA}}* y *{{productB}}* para ver cuál se adapta mejor a lo que buscas?',
  genericBridgeText:
    'Con gusto. ¿Buscas algún producto en especial o te gustaría conocer opciones de nuestro catálogo o recomendaciones?',
  productStatementText: '¿Continuamos con tu orden de *{{product}}* para coordinar la entrega?',
  multipleProductsStatementText: '¿Te gustaría que comparemos las opciones activas para ayudarte a decidir?',
  genericStatementText: '¿Te gustaría ver más opciones de nuestro catálogo o revisar recomendaciones?',
}

export interface CrossSellingRule {
  triggerKeyword: string
  complementItem: string
  offerPhrase?: string | null
}

export interface TenantCommercialPolicy {
  schemaVersion: 1
  belleza?: {
    depositAmount?: number | null
    depositType?: 'fixed' | 'percentage' | null
    depositPaymentMethods?: string | null
    evaluationPolicy?: string | null
    salonAddress?: string | null
  }
  inmobiliaria?: {
    securityDepositMonths?: number | null
    advanceRentMonths?: number | null
    brokerageFeeMonths?: number | null
    naturalPersonRequirements?: string | null
    juridicalPersonRequirements?: string | null
    visitNoticeHours?: number | null
  }
  turismo?: {
    bookingDepositPercentage?: number | null
    checkInTime?: string | null
    checkOutTime?: string | null
    remainingBalancePolicy?: string | null
    boatTourIncluded?: boolean | null
    includedServices?: string | null
  }
  repuestos?: {
    workshopDeliveryTime?: string | null
    counterPickupAddress?: string | null
    partsWarrantyDays?: number | null
    warrantyConditions?: string | null
    nationalCouriers?: string[] | null
    crossSellingEnabled?: boolean | null
    closingQuestionOverride?: string | null
    crossSellingRules?: CrossSellingRule[] | null
  }
  restaurante?: {
    estimatedDeliveryTime?: string | null
    deliveryCostPolicy?: string | null
    coverageZones?: string[] | null
    dineInAvailable?: boolean | null
  }
  bodegon?: {
    coldDeliveryTime?: string | null
    cashChangeAvailability?: string | null
    iceAndSnacksIncluded?: boolean | null
  }
  ferreteria?: {
    heavyFreightDeliveryTime?: string | null
    lightExpressDeliveryTime?: string | null
    warehousePickupAddress?: string | null
  }
  salud?: {
    appointmentConfirmationSla?: string | null
    clinicAddress?: string | null
    labFastingHours?: number | null
    telemedicineAvailable?: boolean | null
  }
  generic?: {
    deliveryPolicyNote?: string | null
    warrantyPolicy?: string | null
    returnPolicy?: string | null
    servicePolicyNote?: string | null
    crossSellingEnabled?: boolean | null
    closingQuestionOverride?: string | null
    crossSellingRules?: CrossSellingRule[] | null
    [key: string]: unknown
  }
}

export type PolicyFieldType = 'text' | 'number' | 'boolean' | 'tags' | 'select'

export interface PolicyFieldDescriptor {
  key: string
  label: string
  type: PolicyFieldType
  group: 'commercial' | 'logistics'
  helperText?: string
  unit?: string
  min?: number
  max?: number
  maxLength?: number
  maxItems?: number
  options?: ReadonlyArray<{ value: string; label: string }>
  /** Si es un tiempo de entrega, cuál de los dos tiempos del checkout alimenta. */
  deliveryTimeRole?: 'local' | 'general'
}

export type PolicyValueSource = 'tenant' | 'platform' | 'unset'

export type PolicyFieldValue = string | number | boolean | string[]

export interface EffectivePolicyField {
  value: PolicyFieldValue | null
  source: PolicyValueSource
  /** Valor de plataforma que se usaría si el tenant vaciara el campo. */
  platformValue: PolicyFieldValue | null
}

export type PolicySectionKey = Exclude<keyof TenantCommercialPolicy, 'schemaVersion'>

/**
 * Vista del vertical que arma el backend (`buildVerticalPolicyView`): qué campos
 * existen, qué valor usa hoy el bot y de dónde viene. El panel no duplica nada de esto.
 */
export interface VerticalPolicyView {
  vertical: string
  section: PolicySectionKey
  descriptors: PolicyFieldDescriptor[]
  crossSelling: boolean
  effective: Record<string, EffectivePolicyField>
  runtime: {
    vertical: string
    requiresAppointment: boolean
    allowPriceDiscussion: boolean
  }
}

export interface ReengagementConfig {
  enabled: boolean
  timeoutMinutes: number
  onlyAfterPriceInquiry: boolean
  messages: string[]
  maxPerConversation: number
}

export const DEFAULT_REENGAGEMENT_CONFIG: ReengagementConfig = {
  enabled: false,
  timeoutMinutes: 20,
  onlyAfterPriceInquiry: true,
  messages: [
    '¿Te gustaría ver fotos del producto? 📸',
    '¿Tienes dudas con los medios de pago? 💳',
    '¿Puedo ayudarte con algo más sobre este producto? 🤔',
    '¿Necesitas más información para decidirte? 📋',
  ],
  maxPerConversation: 2,
}

export type BotSettings = {
  isActive?: boolean
  tone: string
  language: string
  aiEnabled: boolean
  promptBase?: string | null
  model?: string | null
  temperature?: number | null
  maxTokens?: number | null
  botName?: string | null
  avatar?: string | null
  persona?: 'Professional' | 'Friendly' | 'Tech' | null
  welcomeMessage?: string | null
  typingSimulation?: boolean
  /** Confirmar lectura (doble check azul) en WhatsApp, siempre tras una pausa de lectura. */
  sendReadReceipts?: boolean
  /** Reglas propias del negocio para el asistente (opción avanzada). */
  customAgentRules?: string | null
  checkoutConfig?: CheckoutFulfillmentConfig
  commercialPolicyConfig?: TenantCommercialPolicy | null
  /** Solo lectura: la calcula el backend en cada GET/PUT. */
  commercialPolicyView?: VerticalPolicyView
  faqBridgeConfig?: FaqBridgeConfig
  faqBridgePresets?: FaqBridgePreset[]
  faqBridgePresetId?: string | null
  escalationPresetId?: string | null
  escalationPresets?: EscalationPreset[]
  reengagementConfig?: ReengagementConfig
  handoffRecipientLabel?: string | null
  escalationMessage?: string | null
  maxResponsesMessage?: string | null
}

export async function getBotSettings() {
  const { data } = await api.get<BotSettings>('/bot/settings')
  return data
}

/** El backend combina `checkoutConfig` campo a campo: una pantalla puede enviar solo los suyos. */
export type BotSettingsUpdate = Omit<Partial<BotSettings>, 'checkoutConfig'> & {
  checkoutConfig?: Partial<CheckoutFulfillmentConfig>
}

export async function updateBotSettings(payload: BotSettingsUpdate) {
  const { data } = await api.put<BotSettings>('/bot/settings', payload)
  return data
}

export interface TestWebchatSession {
  instanceId: string
  widgetToken: string
  tenantId: string
  tenantSlug: string
  visitorId: string
  botName?: string
}

export interface SendTestMessageResult {
  reply: string
  conversationId: string
  stage?: string
  intent?: string
  selectedProduct?: { id?: string; name?: string; price?: number; imageUrl?: string } | null
  mediaUrl?: string
  activeOrderDraft?: any
  timestamp: string
}

export interface TestChatEvaluationResult {
  tenantId: string
  conversationId: string
  overallScore: number
  conversionOutcome: 'converted' | 'in_progress' | 'abandoned' | 'escalated' | 'lost'
  quantitative: {
    stageProgressionScore: number
    highestStageReached: string
    turnCount: number
    turnEfficiencyScore: number
    frictionScore: number
    commercialSafetyPassed: boolean
    commercialSafetyViolation?: string
  }
  qualitative?: {
    overallScore: number
    conversionOutcome: string
    customerExperienceScore: number
    productClarityScore: number
    objectionHandlingScore: number
    strengths: string[]
    frictionPoints: string[]
    actionableSuggestions: string[]
    summary: string
  }
  evaluatedAt: string
}

export async function getTestChatSession(visitorId?: string): Promise<TestWebchatSession> {
  const params = visitorId ? { visitorId } : {}
  const { data } = await api.get<TestWebchatSession>('/bot/test-chat/session', { params })
  return data
}

export async function sendTestChatMessage(payload: {
  text: string
  visitorId: string
  visitorName?: string
  imageUrl?: string
  mediaUrl?: string
  mediaMimetype?: string
}): Promise<SendTestMessageResult> {
  const { data } = await api.post<SendTestMessageResult>('/bot/test-chat/message', payload)
  return data
}

export async function resetTestChatSession(visitorId: string): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>('/bot/test-chat/reset', { visitorId })
  return data
}

export async function evaluateTestChat(payload: {
  visitorId?: string
  conversationId?: string
  messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string; timestamp?: string | number }>
  stageHistory?: string[]
  finalStage?: string
  selectedProduct?: any
  deepEvaluation?: boolean
}): Promise<TestChatEvaluationResult> {
  const { data } = await api.post<TestChatEvaluationResult>('/bot/test-chat/evaluate', payload)
  return data
}

export const AVAILABLE_MODELS: { label: string; value: string }[] = [
  { label: 'deepseek/deepseek-chat-v3-0324:free', value: 'deepseek/deepseek-chat-v3-0324:free' },
  { label: 'OpenAI GPT-4', value: 'openai/gpt-4' },
  { label: 'Claude 3 Sonnet', value: 'anthropic/claude-3-sonnet' },
  { label: 'Llama 3 70B', value: 'meta/llama-3-70b' },
]


